// @ts-check

// http://localhost:9292/pages/contact?_no-reset=1&_disabled-integrations=native,klaviyo,zendesk&_only-preview=1

const SUPPORTED_INTEGRATIONS = {
  gsheet: {
    type: 'external',
    integrationSettingKeys: ['__endpoint', '_sheet_url', '_sheet_config_key'],
    optionalIntegrationSettingKeys: [],
  },
  klaviyo: {
    type: 'external',
    integrationSettingKeys: ['a', 'g'],
    optionalIntegrationSettingKeys: [],
  },
  zendesk: {
    type: 'external',
    integrationSettingKeys: ['proxy_url'],
    optionalIntegrationSettingKeys: [],
  },
}

const SUPPORTED_INTEGRATIONS_KEYS = Object.keys(SUPPORTED_INTEGRATIONS)

/**
 * @typedef {{
 *  __x_pluggable_form_integrations: any,
 *  Shopify: undefined | {
 *    recaptchaV3: string | undefined
 *    routes: { root: string | undefined } | undefined
 *  }
 * } & Window} CustomWindow
 */

/** @type {CustomWindow} */
const cwindow = /** @type {any} */ (window)

class BaseElement extends HTMLElement {
  constructor() {
    super()
    this._connected = false
    /** @type {AbortController} */
    this._ac = /** @type {any} */ (null)
  }

  async connectedCallback() {
    this._connected = true
    await Promise.resolve()
    if (!this._connected) return

    this._ac && !this._ac.signal.aborted && this._ac.abort()
    this._ac = new AbortController()

    /** @type {any} */
    let x = this.mount()
    if (x instanceof Promise) {
      x = await x
    }

    if (x !== false) {
      this._aftermount()
    }
  }

  mount() {}
  unmount() {}

  _aftermount() {
    this.setAttribute('hydrated', 'true')
  }
  _afterunmount() {
    this.setAttribute('hydrated', 'false')
  }

  async disconnectedCallback() {
    this._connected = false
    await Promise.resolve()
    if (this._connected) return

    this._ac && !this._ac.signal.aborted && this._ac.abort()
    // @ts-expect-error
    this._ac = null
    this.unmount()
    this._afterunmount()
  }
}

/**
 *
 * @param {string[]} list
 * @param {string[]} list2
 * @returns {string[]}
 */
function intersect(list, list2) {
  return list.reduce((acc, item) => {
    if (item != null && !acc.includes(item) && list2.includes(item)) {
      acc.push(item)
    }
    return acc
  }, /** @type {string[]} */ ([]))
}

/**
 *
 * @param {HTMLFormElement} form
 */
function resetForm(form) {
  form.reset()
  form.querySelectorAll('[data-default-value]').forEach((field) => {
    const value = field.getAttribute('data-default-value') || ''
    if (field instanceof HTMLInputElement) {
      if (field.type === 'checkbox' || field.type === 'radio') {
        field.checked = value === 'true' || value === 'on' || value === 'checked'
      }
    }

    if ('value' in field) {
      try {
        field.value = value
      } catch (e) {}
    }
  })
}

function getRawNameAttr(name) {
  if (!name) return ''

  if (name.startsWith('contact[')) {
    name = name.replace('contact[', '').slice(0, -1)
  }
  return name
}

/**
 * @typedef {((formField: HTMLFormElement, key: string, value: string | number | boolean | FormDataEntryValue | null, intId: string | null) => { key: string, value: any} | null)} FormFieldToKeyValue
 */
function formFieldToKeyValue(field, key, value, intId) {
  if (!field || !(field instanceof HTMLElement)) {
    return null
  }
  const originalKey = key

  if (intId) {
    key = field.getAttribute(`data-${intId}-label`)
  }

  if (!key || !intId) {
    key = getRawNameAttr(field.getAttribute('name')) || originalKey
  }

  if (!key || key === 'form_type' || key === 'utf8') {
    return null
  }

  return { key: key, value: value }
}

function formDataToJson(formData) {
  const data = {}

  if (formData instanceof HTMLFormElement) {
    formData = new FormData(formData)
  }

  formData.forEach((value, key) => {
    const parts = key.split('.')

    parts.reduce((acc, key, index) => {
      const list = key.endsWith('[]')
      if (list) key = key.slice(0, -2)

      const isLast = index === parts.length - 1
      if (!isLast) {
        if (!acc[key]) acc[key] = {}
        return acc[key]
      }

      if (!Reflect.has(acc, key)) {
        acc[key] = list ? [value] : value
        return acc
      }
      if (!Array.isArray(acc[key])) {
        acc[key] = [acc[key]]
      }
      acc[key].push(value)
      return acc
    }, data)
  })
  return data
}

/**
 *
 * @param {HTMLFormElement} form
 * @param {string | null} intId
 * @param {null | undefined | FormFieldToKeyValue} [mapper]
 * @returns
 */
function mapFormData(form, intId, mapper) {
  const _mapper = mapper || formFieldToKeyValue

  const defaultFd = form instanceof FormData ? form : new FormData(form)
  const nextFd = new FormData()
  defaultFd.forEach((value, key) => {
    const formField = form.elements[key] || form[key]
    if (!formField) {
      return
    }

    const mapped = _mapper(formField, key, value, intId)
    if (!mapped || mapped.key == null || mapped.value == null) {
      return
    }
    nextFd.set(mapped.key, mapped.value)
  })
  return nextFd
}

/**
 *
 * @param {HTMLFormElement} form
 * @returns
 */
function isShopifyContactForm(form) {
  let action = form.getAttribute('action')

  if (!action) return false

  if (action.startsWith('/contact')) return true

  const shopifyRootRoute = cwindow.Shopify && cwindow.Shopify.routes && cwindow.Shopify.routes.root
  if (shopifyRootRoute) {
    action = action.replace(shopifyRootRoute, '')
    return action.startsWith('/contact')
  }

  return false
}

/**
 *
 * @param {Function} f
 * @returns
 */
function isNativeFunction(f) {
  return f.toString().includes('[native code]')
}

/**
 *
 * @param {HTMLFormElement} form
 * @returns
 */
function triggerNativeFormSubmit(form) {
  // shopify captcha handles the form submission
  // if there is no captcha, we need to manually submit the form
  const shopifyForm = isShopifyContactForm(form)
  if (shopifyForm) {
    const hasCaptcha =
      (cwindow.Shopify && cwindow.Shopify.recaptchaV3) ||
      form.getAttribute('data-cptcha') === 'true' ||
      form.getAttribute('data-recaptcha-bound') === 'true'
    const hasHtmlOnsubmit = typeof form.onsubmit === 'function' || !isNativeFunction(form.submit)

    const needsManualSubmission = !hasCaptcha && !hasHtmlOnsubmit
    console.log('Form needs manual submission?', {
      needsManualSubmission,
      hasCaptcha,
      hasHtmlOnsubmit,
    })
    if (needsManualSubmission) {
      form.submit()
      return true
    }
  }

  try {
    // const action = form.getAttribute("action");
    // if (!action) return false;

    // const actionPathname = action.startsWith("/")
    //   ? action
    //   : new URL(action).pathname;
    // const shopifySubmittable = actionPathname.startsWith("/contact");
    // if (!shopifySubmittable) return false;

    const ev = new CustomEvent('submit', {
      detail: { native_submit: true },
      bubbles: true,
      cancelable: true,
    })

    // @ts-expect-error
    ev.__nativeSubmit = true

    form.dispatchEvent(ev)
    return true
  } catch (e) {
    return false
  }
}

/**
 * @param {XPluggableForm} xPluggableFormElement
 * @param {string} intId
 */
function getIntegrationSettings(xPluggableFormElement, intId) {
  const integrationSettings = Array.from(
    xPluggableFormElement.querySelectorAll(`[data-integration-input="${intId}"]`)
  ).reduce((acc, input) => {
    if (input && 'value' in input) {
      const value = input.value
      const name = input.getAttribute('data-name')
      if (name && value && typeof value === 'string') {
        acc[name] = value
      }
    }
    return acc
  }, /** @type {Record<string, string>} */ ({}))
  return integrationSettings
}

/**
 *
 * @param {XPluggableForm} _pfEl
 * @param wait
 * @returns
 */
function loadIntegrations(_pfEl, wait = false) {
  const pfEl = _pfEl instanceof XPluggableForm ? _pfEl : null

  if (!pfEl) {
    throw new Error('[x-pluggable-form] pfEl must be an instance of XPluggableForm')
  }

  if (pfEl._integrationModulesLoaded) {
    return true
  }

  const integrationKeys = pfEl._integrationKeys

  const promises = []

  cwindow.__x_pluggable_form_integrations = cwindow.__x_pluggable_form_integrations || {}
  for (let i = 0; i < integrationKeys.length; i++) {
    const intId = integrationKeys[i]
    const int = SUPPORTED_INTEGRATIONS[intId]
    if (!int) {
      throw new Error(`[x-pluggable-form] integration "${intId}" not supported`)
    }

    // load the integration module
    if (!cwindow.__x_pluggable_form_integrations[intId]) {
      const src = pfEl.getAttribute(`${intId}-asset-url`)
      promises.push(
        src
          ? import(src).then((mod) => {
              cwindow.__x_pluggable_form_integrations[intId] = mod
            })
          : Promise.reject(new Error(`[x-pluggable-form] Missing integration asset url: ${intId}`))
      )

      if (!src) {
        pfEl.setAttribute(`data-error-${intId}`, `Missing integration`)
      }
    }

    pfEl.allIntegrationSettings[intId] = getIntegrationSettings(pfEl, intId)
  }

  if (promises.length) {
    if (wait) {
      return Promise.allSettled(promises).then(() => {
        pfEl._integrationModulesLoaded = true
        return true
      })
    }
  }
  return true
}

function uid() {
  const id = Math.random().toString(36).slice(2, 7) + Math.random().toString(36).slice(2, 7)
  let newId = ''
  for (let i = 0, len = id.length; i < len; i++) {
    const makeUpper = Math.random() > 0.5
    newId += makeUpper ? id[i].toUpperCase() : id[i]
  }

  return newId
}

/**
 *
 * @param {string} key
 * @returns {boolean}
 */
function isParamEnabled(key) {
  const value = SEARCH_PARAMS.has(key) ? SEARCH_PARAMS.get(key) || 'true' : null
  return value === 'true' || value === '1'
}

const IS_LOCALHOST = window.location.host.startsWith('localhost')
const SEARCH_PARAMS = new URLSearchParams(window.location.search)

const NO_RESET = isParamEnabled('_no-reset')
const ONLY_PREVIEW = isParamEnabled('_only-preview')

const DEV_DISABLED_INTEGRATIONS = (SEARCH_PARAMS.get('_disabled-integrations') || '').split(',')

if (SEARCH_PARAMS.get('_k_dev') === 'skip') {
  DEV_DISABLED_INTEGRATIONS.push('klaviyo')
}

class XPluggableForm extends BaseElement {
  static SUPPORTED_INTEGRATIONS = SUPPORTED_INTEGRATIONS_KEYS

  constructor() {
    super()
    this.btnLoadingClass = 'btn--loading'

    /** @type {string[]} */
    this._integrationKeys = []
    /** @type {string[]} */
    this._disabledIntegrationKeys = []

    /** @type {Record<string, Record<string, string>>} */
    this.allIntegrationSettings = {}

    this._integrationModulesLoaded = false

    // to use in another scripts
    this.formDataToJson = formDataToJson

    this._allowNativeSubmit = false

    this._onlyPreview = ONLY_PREVIEW
  }

  /**
   *
   * @param {'idle' | 'pending' | 'idle success' | 'idle error'} status
   */
  _setStatus(status) {
    this.setAttribute('status', status)
    if (this.form) {
      this.form.setAttribute('data-status', status)
    }

    this.disableElements(status === 'pending')
  }

  mount() {
    const parent = this.parentElement
    const form = parent instanceof HTMLFormElement ? parent : null
    if (!form) {
      this.setAttribute('data-error', 'Missing form')
      return false
    }

    this.form = form
    this.submitBtns = Array.from(form.querySelectorAll('[type="submit"]'))
    const loadingClass = this.getAttribute('btn-loading-class')
    if (loadingClass) this.btnLoadingClass = loadingClass

    this._disabledIntegrationKeys = [
      ...DEV_DISABLED_INTEGRATIONS,
      ...(this.getAttribute('disabled-integrations') || '').split(','),
    ].filter((x) => x && (x === 'native' || SUPPORTED_INTEGRATIONS_KEYS.includes(x)))

    this._integrationKeys = intersect(
      (this.getAttribute('integrations') || '')
        .split(',')
        .filter((k) => k && !this._disabledIntegrationKeys.includes(k)),
      SUPPORTED_INTEGRATIONS_KEYS
    )
    this.setAttribute('integrations', this._integrationKeys.join(','))
    this.setAttribute('disabled-integrations', this._disabledIntegrationKeys.join(','))
    if (ONLY_PREVIEW) {
      this.setAttribute('data-only-preview', 'true')
    }

    this._allowNativeSubmit = isShopifyContactForm(form)
    if (this._disabledIntegrationKeys.includes('native') || ONLY_PREVIEW) {
      this._allowNativeSubmit = false
      form.removeAttribute('action')
    }

    loadIntegrations(this)

    this._setStatus('idle')

    form.addEventListener(
      'submit',
      async (e) => {
        if (!form.checkValidity()) {
          form.reportValidity()

          e.preventDefault()
          e.stopPropagation()
          return
        }

        if (this._allowNativeSubmit) {
          if (
            '__nativeSubmit' in e ||
            (e instanceof CustomEvent && e.detail && e.detail.native_submit)
          ) {
            // - `triggerNativeFormSubmit` triggered an event
            // don't do anything - just let it bubble and shopify will handle it

            return
          }
        }

        e.preventDefault()
        e.stopPropagation()

        this._setStatus('pending')

        return this._submitToIntegrations()
          .then(() => {
            const formData = mapFormData(form, null)
            const formJson = formDataToJson(formData)
            document.dispatchEvent(
              new CustomEvent('x-pluggable-form::submit-complete', {
                detail: {
                  element: this,
                  form,
                  formData,
                  formJson,
                  integrationKeys: this._integrationKeys,
                  integrationSettings: this.allIntegrationSettings,
                },
              })
            )

            if (this._allowNativeSubmit) {
              // successfully triggered? don't handle success case
              if (triggerNativeFormSubmit(form)) {
                return
              }
            }

            this._setStatus('idle success')

            const canRest = !(NO_RESET || ONLY_PREVIEW)
            if (canRest) {
              resetForm(form)
            }
          })
          .catch((e) => {
            console.error(e)
            this._setStatus('idle error')
          })
      },
      { signal: this._ac.signal, capture: true }
    )

    return true
  }

  /**
   *
   * @returns {Promise<PromiseSettledResult<any>[]>}
   */
  async _submitToIntegrations() {
    const form = this.form
    if (!form) throw new Error(`No form found`)

    if (ONLY_PREVIEW && !this._integrationKeys.length) {
      const fd = mapFormData(form, null)
      const formJson = formDataToJson(fd)
      console.log('Form JSON => ', formJson)
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve([{ status: 'fulfilled', value: formJson }])
        }, 500)
      })
    }

    await loadIntegrations(this, true)

    const submissionUid = uid()
    const integrationKeys = this._integrationKeys
    const ranIntegration = []
    const promises = []

    for (let i = 0; i < integrationKeys.length; i++) {
      const intId = integrationKeys[i]

      const intModule = intId ? cwindow.__x_pluggable_form_integrations[intId] : null

      if (!intModule || typeof intModule.submit !== 'function') {
        const errorMsg = !intModule
          ? `integration modeule not found`
          : intModule && typeof intModule.submit !== 'function'
          ? `integration module does not have a submit function`
          : null

        const msg = `[x-pluggable-form] "${intId}" : ${errorMsg}`

        console.warn(msg, { integration_key: intId, integration: intModule })
        promises.push(Promise.reject(new Error(msg)))
        continue
      }

      // const integrationSettings = Array.from(
      //   this.querySelectorAll(`[data-integration-input="${intId}"]`)
      // ).reduce((acc, input) => {
      //   if (input && "value" in input) {
      //     const value = input.value;
      //     const name = input.getAttribute("data-name");
      //     if (name && value && typeof value === "string") {
      //       acc[name] = value;
      //     }
      //   }
      //   return acc;
      // }, /** @type {Record<string, string>} */ ({}));
      const integrationSettings =
        this.allIntegrationSettings[intId] || getIntegrationSettings(this, intId)

      ranIntegration.push(intId)
      const getFormData = mapFormData.bind(null, form, intId, formFieldToKeyValue)

      promises.push(
        ONLY_PREVIEW
          ? { integrationSettings, json: formDataToJson(getFormData()) }
          : intModule.submit({
              form: this.form,
              root: this,
              integrationSettings,
              getFormData,
              formDataToJson,
              submissionUid,
            })
      )
    }

    if (promises.length === 0) {
      console.warn('[x-pluggable-form] No integrations found')
      return promises
    }

    const results = await Promise.allSettled(promises)
    ranIntegration.forEach((intId, i) => {
      const result = results[i]
      const reason = result.status === 'rejected' ? result.reason : undefined
      const logFn = result.status === 'rejected' ? 'error' : 'log'

      console[logFn]({
        integration: intId,
        status: result.status,
        reason,
        result,
      })
    })

    return results
  }

  disableElements(pending) {
    const btns = this.submitBtns
    if (btns && btns.length > 0 && this.btnLoadingClass) {
      btns.forEach((btn) => {
        btn.classList.toggle(this.btnLoadingClass, !!pending)
        const fnName = pending ? 'setAttribute' : 'removeAttribute'
        btn[fnName]('disabled', '')
        btn[fnName]('aria-busy', pending ? 'true' : 'false')
      })
    }
    const form = this.form
    if (form) {
      pending ? form.setAttribute('disabled', '') : form.removeAttribute('disabled')
    }
  }
}

if (!window.customElements.get('x-pluggable-form')) {
  window.customElements.define('x-pluggable-form', XPluggableForm)
}
